import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Cap } from '../../primitives'
import { CONFIGURATIONS, FIELD_ROWS, Guarantee, STAGE_ROWS } from './stateMatrixData'

const GUARANTEE_LABEL: Record<Guarantee, string> = {
  [Guarantee.ALWAYS]: 'Always',
  [Guarantee.SOMETIMES]: 'Sometimes',
  [Guarantee.NEVER]: 'Never',
}

const GUARANTEE_VARIANT = {
  [Guarantee.ALWAYS]: 'success',
  [Guarantee.SOMETIMES]: 'outline',
  [Guarantee.NEVER]: 'destructive',
} as const

function GuaranteeBadge({ value }: { value: Guarantee }) {
  return (
    <Badge variant={GUARANTEE_VARIANT[value]} className="text-[10px]">
      {GUARANTEE_LABEL[value]}
    </Badge>
  )
}

function ConfigCards() {
  return (
    <div className="grid gap-3 md:grid-cols-3">
      {CONFIGURATIONS.map((config) => (
        <div key={config.key} className="border-ink-200 flex flex-col gap-2 rounded-lg border p-3">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-ink text-xs font-bold">{config.label}</span>
            <GuaranteeBadge value={config.cardAtFirstPaint} />
          </div>
          <p className="text-ink-400 font-mono text-[10px] leading-relaxed">{config.vendors}</p>
          <div className="flex flex-col gap-1.5">
            <div>
              <Cap>Card at first paint</Cap>
              <p className="text-ink-600 mt-0.5 text-xs leading-relaxed">{config.cardAtFirstPaintNote}</p>
            </div>
            <div>
              <Cap>Names the card</Cap>
              <p className="text-ink-600 mt-0.5 text-xs leading-relaxed">{config.carrierName}</p>
            </div>
            <div>
              <Cap>broker</Cap>
              <p className="text-ink-600 mt-0.5 text-xs leading-relaxed">{config.brokerValue}</p>
            </div>
            <div>
              <Cap>Name swap</Cap>
              <p className="text-ink-600 mt-0.5 text-xs leading-relaxed">{config.nameSwap}</p>
            </div>
            <div>
              <Cap>Priced within</Cap>
              <p className="text-ink-600 mt-0.5 text-xs leading-relaxed">{config.happyPath}</p>
            </div>
            <div>
              <Cap>Long tail</Cap>
              <p className="text-ink-600 mt-0.5 text-xs leading-relaxed">{config.tail}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

function StageTable() {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Stage</TableHead>
          <TableHead>Derived from</TableHead>
          <TableHead>Shape</TableHead>
          {CONFIGURATIONS.map((config) => (
            <TableHead key={config.key}>{config.label}</TableHead>
          ))}
          <TableHead>Note</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {STAGE_ROWS.map((row) => (
          <TableRow key={row.stage}>
            <TableCell className="font-mono text-xs whitespace-nowrap">{row.stage}</TableCell>
            <TableCell className="text-ink-500 text-xs">{row.from}</TableCell>
            <TableCell className="text-ink-500 text-xs whitespace-nowrap">
              {row.minimal ? 'Minimal' : 'Priced'}
            </TableCell>
            {CONFIGURATIONS.map((config) => (
              <TableCell key={config.key}>
                <GuaranteeBadge value={row.reach[config.key]} />
              </TableCell>
            ))}
            <TableCell className="text-ink-500 max-w-xs text-xs leading-relaxed">{row.note}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

function FieldTable() {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Field</TableHead>
          <TableHead>Minimal</TableHead>
          <TableHead>Priced</TableHead>
          <TableHead>Note</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {FIELD_ROWS.map((row) => (
          <TableRow key={row.field}>
            <TableCell className="font-mono text-xs">{row.field}</TableCell>
            <TableCell>
              <GuaranteeBadge value={row.minimal} />
            </TableCell>
            <TableCell>
              <GuaranteeBadge value={row.priced} />
            </TableCell>
            <TableCell className="text-ink-500 max-w-md text-xs leading-relaxed">{row.note}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

/**
 * What each card stage can rely on, per configuration. Pre-price cards split on
 * dateSubmitted, stamped when the application actually goes out — a staged
 * wholesaler row holds a vendor id while still unsent, so it stays PREPARING. Only
 * the manual configuration ever swaps a wholesaler name for a carrier name.
 */
export function QuoteCardStateMatrix() {
  return (
    <section className="mb-10 flex flex-col gap-6">
      <div className="flex flex-col gap-3">
        <Cap>Configurations</Cap>
        <ConfigCards />
      </div>

      <div className="flex flex-col gap-3">
        <Cap>Stage reachability</Cap>
        <StageTable />
      </div>

      <div className="flex flex-col gap-3">
        <Cap>Data guaranteed by shape</Cap>
        <FieldTable />
      </div>

      <div className="border-ink-200 flex flex-col gap-2 rounded-lg border border-dashed p-3">
        <Cap>To design around</Cap>
        <p className="text-ink-600 text-xs leading-relaxed">
          A Rocklake submission produces one card per carrier on a hardcoded panel, all appearing at once
          after the submit round-trip. The page can look finished before those arrive.
        </p>
      </div>
    </section>
  )
}
